import type { UserRole, AccountStatus } from "@restaurant-staff-portal/shared";

/**
 * Utility functions for user role and status badges
 * Used across multiple components like UserTable, ProfilePage, etc.
 */

export const getRoleTooltip = (role: UserRole | string): string => {
  switch (role) {
    case "ADMIN":
      return "Full system administration access with all permissions";
    case "MANAGER":
      return "Management capabilities with supervision and reporting access";
    case "SUPERVISOR":
      return "Team supervision and limited administrative functions";
    case "SERVER":
      return "Service role with customer interaction capabilities";
    case "USER":
      return "Standard user access with basic functionality";
    default:
      return "User role information";
  }
};

export const getStatusTooltip = (status: AccountStatus | string): string => {
  switch (status) {
    case "ACTIVE":
      return "Account is active and fully functional";
    case "UNVERIFIED":
      return "Account email address has not been verified yet";
    case "CREATED":
      return "Account has been created but setup is not complete";
    case "ONBOARDING":
      return "Onboarding email has been sent, awaiting user activation";
    case "SUSPENDED":
      return "Account has been temporarily suspended by an administrator";
    case "LOCKED":
      return "Account has been locked due to security concerns";
    case "ARCHIVED":
      return "Account has been archived and is no longer active";
    default:
      return "Account status information";
  }
};

export const getRoleColor = (
  role: UserRole | string,
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (role) {
    case "ADMIN":
      return "secondary";
    case "MANAGER":
      return "primary";
    case "SUPERVISOR":
      return "info";
    case "SERVER":
      return "success";
    case "USER":
      return "default";
    default:
      return "default";
  }
};

export const getStatusColor = (
  status: AccountStatus | string,
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "SUSPENDED":
    case "LOCKED":
      return "error";
    case "UNVERIFIED":
      return "warning";
    case "CREATED":
      return "info";
    case "ONBOARDING":
      return "primary";
    case "ARCHIVED":
      return "default"; // Gray/muted styling for archived users
    default:
      return "default";
  }
};
